import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "./useFilters";
import { getCountByState } from "../services/apiService";
import { updatedMarketData } from "../graph/graphs/HexbinGraph/Hexagon/data";

// The set of state abbreviations that actually appear on the hex map. The legend
// buckets only ever select from these, so the resolver stays in sync with the
// map even if the API returns extra states/territories.
const HEX_STATE_ABBREVS = new Set(
  (updatedMarketData?.data || []).map((market) => market.abbrev).filter(Boolean),
);

// Mirrors the density-bucket thresholds used when the map is rendered
// (LeftSection.fetchStatesData). Kept here so the legend selection can be
// resolved to a states list without that list living in the URL.
const matchesBucket = (caseDensity, legendFilter) => {
  if (legendFilter === "high") return caseDensity > 470;
  if (legendFilter === "medium") return caseDensity > 400 && caseDensity <= 470;
  if (legendFilter === "low") return caseDensity <= 400;
  return false;
};

// Resolves a legend filter (low/medium/high/partner) into the list of state
// abbreviations it selects, from the count_by_state rows.
export const resolveLegendStates = (rows, legendFilter) => {
  if (!legendFilter || !Array.isArray(rows)) return [];
  return rows
    .filter((row) => HEX_STATE_ABBREVS.has(row?.state))
    .filter((row) => {
      if (legendFilter === "partner") return !!row?.has_partner;
      const caseDensity = Math.round(Number(row?.case_density)) || 0;
      return matchesBucket(caseDensity, legendFilter);
    })
    .map((row) => row.state);
};

// Single source of truth for "which states are currently selected", whether the
// selection came from an explicit `states` URL param (hex click / state search)
// or from a legend filter (in which case the states are derived, not stored in
// the URL). Consumers should use `effectiveStates` for API calls and headers.
export function useResolvedStates() {
  const { states, legendFilter, partnerExclusiveFlag, globalFilterValue, quickFilter } =
    useFilters();

  // Only fetch the per-state data when a legend filter is active (otherwise the
  // explicit `states` param is authoritative and no derivation is needed).
  const countByStateParams = {
    partnerExclusiveFlag,
    globalFilter: globalFilterValue,
    quickFilter,
  };
  const { data: countByState } = useQuery({
    queryKey: ["count-by-state", countByStateParams],
    enabled: !!legendFilter,
    queryFn: ({ signal }) => getCountByState(countByStateParams, signal),
  });

  const legendStates = useMemo(
    () => resolveLegendStates(countByState?.data, legendFilter),
    [countByState, legendFilter],
  );

  const effectiveStates = legendFilter ? legendStates : states;

  // True while a legend filter is active but its per-state data hasn't loaded
  // yet. The count_by_state query is keyed on quickFilter/globalFilter/partner,
  // so changing any of those re-keys it and `data` is momentarily undefined —
  // during which `effectiveStates` is empty. Consumers can use this to defer
  // state-scoped fetches instead of falling back to an all-hospitals request.
  const legendPending = !!legendFilter && !countByState?.data;

  return { states, legendFilter, legendStates, effectiveStates, legendPending };
}
